import PersonalMemoMenuItem from '../../components/common/personalMemos/PersonalMemoMenuItem';
import ChatMessageActionMenu from '../../components/common/chat/topics/ChatMessageActionMenu';
import ChatTimelineLoading from '../../components/common/chat/shared/ChatTimelineLoading';
import React from 'react';
import { withUnreadDivider } from '../../components/common/chat/timeline/chatUnreadDivider';
import AdminSidebar from '../../components/admin/Sidebar';
import { ADMIN_MESSAGE_FORMATS } from '../../constants/messageFormats';
import ChatComposer from '../../components/common/chat/composer/ChatComposer';
import ChatImageModal from '../../components/common/chat/composer/ChatImageModal';
import ChatTargetListRow from '../../components/common/chat/sidepanel/ChatTargetListRow';
import ChatMentionTaskNotice from '../../components/common/chat/mentions/ChatMentionTaskNotice';
import { renderChatSearchHighlight } from '../../components/common/chat/search/chatSearchHighlight';
import ChatMentionTaskResolutionModal from '../../components/common/chat/mentions/ChatMentionTaskResolutionModal';
import ChatHeaderOverflowMenu from '../../components/common/chat/shared/ChatHeaderOverflowMenu';
import ChatAreaHeader from '../../components/common/chat/shared/ChatAreaHeader';
import ChatLinksModal from '../../components/common/chat/assets/ChatLinksModal';
import ChatMediaModal from '../../components/common/chat/assets/ChatMediaModal';
import { adminPersonalChatAssetSource } from '../../api/chatAssets';
import ChatTargetSidebarLoading from '../../components/common/chat/sidepanel/ChatTargetSidebarLoading';
import ChatTargetSidebarToolbar from '../../components/common/chat/sidepanel/ChatTargetSidebarToolbar';
import ChatTargetSwitchFab from '../../components/common/chat/sidepanel/ChatTargetSwitchFab';
import ChatProfileModalLayer from '../../components/common/chat/members/ChatProfileModalLayer';
import ChatAnnouncementBar from '../../components/common/chat/announcements/ChatAnnouncementBar';
import ChatAnnouncementListModal from '../../components/common/chat/announcements/ChatAnnouncementListModal';
import ChatMessageReactionBar from '../../components/common/chat/reactions/ChatMessageReactionBar';
import ChatMessageMenuReactionPicker from '../../components/common/chat/reactions/ChatMessageMenuReactionPicker';
import ChatMessageReactionDetailModal from '../../components/common/chat/reactions/ChatMessageReactionDetailModal';
import ChatReplySnippet from '../../components/common/chat/composer/ChatReplySnippet';
import ChatHeaderIconButton from '../../components/common/chat/shared/ChatHeaderIconButton';
import { formatItemCount } from '../../utils/datetime';
import ChatNoteSystemMessageRow from '../../components/common/chat/notes/ChatNoteSystemMessageRow';
import QuickTaskModal from '../../components/admin/tasks/QuickTaskModal';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import { formatDateTimeJST, formatDateJPFromAny, toYmdSlash } from '../../utils/datetime';
import ChatDragOverlay from '../../components/common/chat/composer/ChatDragOverlay';
import ChatEmptyState from '../../components/common/chat/shared/ChatEmptyState';
import {
  AdminArtistChatGuidanceMessage,
  ChatAnnouncementAvatar,
} from '../../components/common/chat/timeline/ArtistChatGuidanceSystemMessages';
import ChatWorkspaceLayout from '../../components/common/chat/shared/ChatWorkspaceLayout';
import ChatSidePanelToggleButton from '../../components/common/chat/sidepanel/ChatSidePanelToggleButton';
import ChatSidePanelHeader from '../../components/common/chat/sidepanel/ChatSidePanelHeader';
import ChatFollowupInbox from '../../components/common/chat/followups/ChatFollowupInbox';
import ChatFollowupNotificationMenu from '../../components/common/chat/followups/ChatFollowupNotificationMenu';
import AiFluidMark from '../../components/common/chat/shared/AiFluidMark';
import AnnouncementSummarySidePanelCard from '../../components/common/chat/sidepanel/AnnouncementSummarySidePanelCard';
import ArtistProfileSidePanelCard from '../../components/common/chat/members/ArtistProfileSidePanelCard';
import IndividualStandardMessageRow from '../../components/common/chat/timeline/IndividualStandardMessageRow';
import AdminSenderName from '../../components/common/chat/timeline/AdminSenderBadge';
import AdminPersonalNotesPanel from '../../components/admin/chats/AdminPersonalNotesPanel';
import { ChatPageSearchModal, ChatPageSettingsModal } from '../../components/admin/chats/chatPageModals';
import {
  getSystemAvatarIconClass,
  getSystemAvatarToneClass,
  getSystemToneClass,
} from '../../components/common/chat/timeline/systemMessageUtils';
import { appendVariant } from '../../components/common/chat/composer/chatAssetUtils';
import { renderIndividualStandardMessageContent, resolveIndexedImagePreview } from '../../components/common/chat/timeline/individualMessageContentRenderer';
import { renderIndividualSystemMessage } from '../../components/common/chat/timeline/individualSystemMessageRenderer';
import { isGroupNoteMessage } from '../../components/common/chat/notes/groupNoteMessageUtils';
import { shouldShowAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import {
  buildAdminIndividualMessagePresentation,
  resolveAdminIndividualBubbleClassName,
} from '../../components/common/chat/timeline/chatMessagePresentationUtils';
import {
  CHAT_FOLLOWUP_AI_TARGET_ID,
  resolveChatFollowupBadgeToneClass,
} from '../../components/common/chat/followups/chatFollowupApi';
import { API_URL, ARTIST_PROFILE_IMAGE_PATH, buildAssetUrl, buildFlyerUrl, buildMessageImageUrl, normalizeArtistId, isSameArtistId, normalizeMessageId, getMessageDedupKey, isAdminPersonalOutgoingRead } from '../../components/admin/chats/chatPageHelpers';
import {
  renderAdminSenderAvatar,
  renderAdminSenderMeta,
  resolveAdminSenderDisplayName,
} from '../../components/admin/chats/chatPageParts';
import { normalizeAvatarImageValue } from '../../components/common/avatar/PersonAvatar';

export default function AdminChatPageView({ context }) {
  const [showLinksModal, setShowLinksModal] = React.useState(false);
  const [showMediaModal, setShowMediaModal] = React.useState(false);
  const chatAssetSource = React.useMemo(
    () => (context?.selectedArtistId ? adminPersonalChatAssetSource(context.selectedArtistId) : null),
    [context?.selectedArtistId],
  );
  const {
    chatTopics,    activeAnchorMessageId,
    adminGroupChatMessageCount,
    adminGroupChatTone,
    announcementActionMessageId,
    announcementCount,
    announcementSummaries,
    announcements,
    applyTargetSearchModal,
    artistChatMuteLoading,
    artistChatMuted,
    artistDetail,
    artistDetailLoading,
    artistsLoading,
    artistsSentinelRef,
    assigneeQuickToggles,
    canAnnouncementMessage,
    canCopyMessage,
    canCreateQuickTaskFromMessage,
    canManagePersonalNotes,
    canReactMessage,
    canReplyMessage,
    canSwitchToGroupChat,
    canUnsendMessage,
    canViewPersonalNotes,
    chatContainerOuterRef,
    chatContainerRef,
    clearMessageLongPress,
    clearSidebarContextMenuBlock,
    clearSidebarLongPress,
    clearTargetSearchModal,
    clearToolbarSearch,
    closeChatSettingsModal,
    closeMessageMenu,
    closeModal,
    closeProfileModal,
    closeReactionDetail,
    closeTargetMenu,
    currentImageIndex,
    displayMessages,
    unreadDividerMessageId,
    displayedArtists,
    extractFirstUrl,
    followupBadgeCount,
    followupRefreshKey,
    followupSummary,
    formatMessageContent,
    formatSidebarTimestamp,
    getArtistLatestTimestamp,
    getCachedFlyerUrl,
    getGenreColor,
    getGenreLabel,
    getGenreTextColor,
    getHoldDeclineLink,
    getMessageClass,
    getProfileImageStyle,
    getSidebarPreviewText,
    goBackMobile,
    groups,
    handleArtistClick,
    handleArtistMuteToggle,
    handleBannerClick,
    handleComposerSent,
    handleCopyMessage,
    handleDownload,
    handleGoArtistDetail,
    handleImageClick,
    handleJumpToReplyMessage,
    handleMentionClick,
    handleMessageContextMenu,
    handleOpenGroupChatFromTargetList,
    handleMessageMenuReactionSelect,
    handleMessageTouchStart,
    handleNicknamePermissionChange,
    handleOpenAiTask,
    handleOpenInlinePartialCopy,
    handleOpenQuickTaskFromMessage,
    handleOpenSettingsFromTargetMenu,
    handleReadNotifyToggle,
    handleSelectAnnouncementMessage,
    handleSelectReplyMessage,
    handleSettingsButtonClick,
    handleSidebarTouchStart,
    handleToggleAnnouncementMessage,
    handleTogglePinnedFromTargetMenu,
    handleUnsendMessage,
    handleUploading,
    hasMore,
    imageFullUrls,
    imageIndexByMessageId,
    imageMessages,
    imageModalItems,
    imagePreviewUrls,
    inlineCopyMessageId,
    isAiTargetSelected,
    isAnnouncedMessage,
    isAnnouncementListOpen,
    isDragActive,
    isMobile,
    isModalOpen,
    isNicknameAllowed,
    isPinnedArtist,
    isReactionDetailOpen,
    isReactionPending,
    isReadNotifyEnabled,
    isReadOnly,
    isSidePanelLayoutOpen,
    isSidePanelLoading,
    isSidePanelOpen,
    isSystemFormat,
    isTargetSearchMode,
    latestAnnouncement,
    loggedInAdminId,
    loadingMentionResolutionTargetKey,
    loadingOlderMessages,
    mentionResolutionModalProps,
    messageFilter,
    messageMenu,
    messageMenuRef,
    mobileBackButtonClassName,
    mobileView,
    noteCount,
    openAiInbox,
    openMentionResolution,
    openReactionDetail,
    openSelectedArtistProfile,
    openTargetMenuAt,
    openTargetSearchModal,
    overlayRect,
    parseReminderContent,
    parts,
    pendingMentionTargets,
    personalMentionCandidates,
    pickFlyerFilename,
    profileTarget,
    quickTaskSource,
    reactionDetailGroups,
    reactionDetailLoading,
    readEnabledAdmins,
    readNotifyFetching,
    readOptInCacheRef,
    readToggleLoading,
    refreshNoteCount,
    renderPartialCopyContent,
    replyDraft,
    resolveMentionTarget,
    resolvePrimaryGenreObj,
    resolvingMentionTargetId,
    rootClasses,
    searchDraft,
    searchSummaryLabel,
    searchToolbarChips,
    selectedArtist,
    selectedArtistId,
    sendingBusy,
    setIsAnnouncementListOpen,
    setIsSidePanelOpen,
    setQuickTaskSource,
    setReplyDraft,
    setSearchDraft,
    setShowNotes,
    setShowSearchModal,
    setSidebarEl,
    settingsArtist,
    shouldShowChatGuidance,
    shouldShowSidePanel,
    shouldSuppressArtistUnread,
    showDelayedInitialLoading,
    showNewMsgBanner,
    showNextImage,
    showNotes,
    showPrevImage,
    showSearchModal,
    showSettingsModal,
    sidebarPressRef,
    sidebarRef,
    targetMenu,
    targetMenuRef,
    toastError,
    toastSuccess,
    toggleReaction,
    unpinAnnouncement,
  } = context;
  const followupBadgeToneClass =
    followupBadgeCount > 0 ? resolveChatFollowupBadgeToneClass(followupSummary) : '';

  const [followupNotificationMenu, setFollowupNotificationMenu] = React.useState(null);
  const closeFollowupNotificationMenu = React.useCallback(() => {
    setFollowupNotificationMenu(null);
  }, []);
  const openFollowupNotificationMenuAt = React.useCallback((clientX, clientY) => {
    setFollowupNotificationMenu({ x: clientX, y: clientY });
  }, []);
  const blockFollowupContextMenu = React.useCallback(() => {
    clearSidebarContextMenuBlock();
    sidebarPressRef.current.suppressContextMenu = true;
    sidebarPressRef.current.contextMenuTimer = window.setTimeout(() => {
      sidebarPressRef.current.suppressContextMenu = false;
      sidebarPressRef.current.contextMenuTimer = null;
    }, 700);
  }, [clearSidebarContextMenuBlock, sidebarPressRef]);
  const handleAiTargetTouchStart = React.useCallback((event) => {
    sidebarPressRef.current.suppressClick = false;
    clearSidebarContextMenuBlock();
    clearSidebarLongPress();
    if (!isMobile) return;
    const touch = event.touches?.[0];
    const clientX = touch?.clientX ?? window.innerWidth / 2;
    const clientY = touch?.clientY ?? 72;
    sidebarPressRef.current.timer = window.setTimeout(() => {
      sidebarPressRef.current.timer = null;
      sidebarPressRef.current.suppressClick = true;
      blockFollowupContextMenu();
      openFollowupNotificationMenuAt(clientX, clientY);
    }, 520);
  }, [
    blockFollowupContextMenu,
    clearSidebarContextMenuBlock,
    clearSidebarLongPress,
    isMobile,
    openFollowupNotificationMenuAt,
    sidebarPressRef,
  ]);

  return (<div className="admin-dashboard">
      <AdminSidebar />
      <div className={rootClasses}>
        <ChatWorkspaceLayout className="admin-chat-workspace">
        {/* ===== Left: Artists list ===== */}
        <div
          className="admin-message-sidebar chat-target-sidebar"
          ref={(el) => {
            sidebarRef.current = el;
            setSidebarEl(el);
          }}
        >
          <ChatTargetSidebarToolbar
            onOpenSearch={openTargetSearchModal}
            summary={searchSummaryLabel}
            chips={searchToolbarChips}
            toggles={assigneeQuickToggles}
          />

          <ul aria-busy={artistsLoading ? 'true' : 'false'}>
            {isTargetSearchMode ? (
              <li className="event-list__result-header">
                <span className="event-list__result-count">
                  <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                  検索結果 {formatItemCount(displayedArtists.length || 0)}
                </span>
                <button
                  type="button"
                  className="event-list__result-clear"
                  onClick={clearToolbarSearch}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                  <span>解除</span>
                </button>
              </li>
            ) : (
              <ChatTargetListRow
                key={CHAT_FOLLOWUP_AI_TARGET_ID}
                className={isAiTargetSelected ? 'active' : ''}
                classNames={{
                  center: 'artist-item-center chat-followup-target-row__center',
                  right: 'artist-item-right chat-followup-target-row__side',
                }}
                onClick={() => {
                  if (sidebarPressRef.current.suppressClick) {
                    sidebarPressRef.current.suppressClick = false;
                    return;
                  }
                  openAiInbox();
                }}
                onContextMenu={(event) => {
                  if (sidebarPressRef.current.suppressContextMenu) {
                    event.preventDefault();
                    event.stopPropagation();
                    clearSidebarContextMenuBlock();
                    return;
                  }
                  event.preventDefault();
                  event.stopPropagation();
                  closeTargetMenu();
                  sidebarPressRef.current.suppressClick = true;
                  openFollowupNotificationMenuAt(event.clientX, event.clientY);
                  setTimeout(() => {
                    sidebarPressRef.current.suppressClick = false;
                  }, 0);
                }}
                onTouchStart={handleAiTargetTouchStart}
                onTouchEnd={clearSidebarLongPress}
                onTouchCancel={clearSidebarLongPress}
                onTouchMove={clearSidebarLongPress}
                avatar={(
                  <AiFluidMark variant="sidebar" className="chat-followup-target-row__eventAiMark" />
                )}
                title="CasticartAI"
                badge={followupBadgeCount > 0 ? (followupBadgeCount > 99 ? '99+' : followupBadgeCount) : null}
                badgeClassName={followupBadgeToneClass}
              />
            )}
            {artistsLoading && displayedArtists.length === 0 ? (
              <ChatTargetSidebarLoading variant="row" count={6} />
            ) : null}
            {displayedArtists.map((artist, artistIndex) => {
              const preview = getSidebarPreviewText(artist);
              const isMessageHit = !!String(messageFilter || '').trim() && !!normalizeMessageId(artist?.search_hit_message_id);
              const latestTs = getArtistLatestTimestamp(artist);
              const timeLabel = formatSidebarTimestamp(latestTs);
                  const isNotifyMuted = !!artist.admin_muted;
                  const readNotifyKey = normalizeArtistId(artist.artist_id);
                  const rowReadNotifyEnabled = artist?.read_notify_enabled ?? artist?.readNotifyEnabled;
                  const readNotifyKnown =
                    readOptInCacheRef.current.has(readNotifyKey) ||
                    rowReadNotifyEnabled === true ||
                    rowReadNotifyEnabled === false;
                  const readNotifyEnabled = readOptInCacheRef.current.has(readNotifyKey)
                    ? readOptInCacheRef.current.get(readNotifyKey) === true
                    : rowReadNotifyEnabled === true;
                  const unreadCount = shouldSuppressArtistUnread(artist?.artist_id)
                    ? 0
                    : Number(artist?.unread_messages || 0);
              const showUnreadBadge = unreadCount > 0 && readNotifyKnown;
              const unreadBadgeToneClass = readNotifyEnabled ? 'badge-tone--alert' : 'badge-tone--ok';
              const isBlocked =
                artist.is_deleted || artist.is_banned || artist.is_ban_requested || artist.is_deleted_request;
              // 検索/絞り込み中は受信箱の未対応メンションCTAを結果に混ぜない
              const mentionTask = isTargetSearchMode
                ? null
                : pendingMentionTargets[normalizeArtistId(artist.artist_id)] || null;

              return (
                <ChatTargetListRow
                  topicScope="individual" topicConversationId={artist.artist_id}
                  hasTopics={artist.has_topics || (String(artist.artist_id) === chatTopics.conversationId && chatTopics.rows.length > 0)}
                  onTopicSelect={(topic) => { chatTopics.selectTargetTopic(artist.artist_id, topic); handleArtistClick(artist); }}
                  key={
                    normalizeMessageId(artist?.search_hit_message_id)
                      ? `${normalizeArtistId(artist?.artist_id)}-${normalizeMessageId(
                          artist?.search_hit_message_id,
                        )}`
                      : `${normalizeArtistId(artist?.artist_id)}-${artistIndex}`
                  }
                  className={`artist-casting-tone--${artist.casting_status_tone || 'none'} ${
                    !isAiTargetSelected && selectedArtist && isSameArtistId(selectedArtist.artist_id, artist.artist_id)
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => {
                    if (sidebarPressRef.current.suppressClick) {
                      sidebarPressRef.current.suppressClick = false;
                      return;
                    }
                    handleArtistClick(artist, {
                      anchorMessageId: String(artist?.search_hit_message_id || '').trim(),
                    });
                  }}
                  onContextMenu={(event) => {
                    if (sidebarPressRef.current.suppressContextMenu) {
                      event.preventDefault();
                      event.stopPropagation();
                      clearSidebarContextMenuBlock();
                      return;
                    }
                    event.preventDefault();
                    sidebarPressRef.current.suppressClick = true;
                    openTargetMenuAt(artist, event.clientX, event.clientY);
                    setTimeout(() => {
                      sidebarPressRef.current.suppressClick = false;
                    }, 0);
                  }}
                  onTouchStart={(event) => {
                    sidebarPressRef.current.suppressClick = false;
                    clearSidebarContextMenuBlock();
                    handleSidebarTouchStart(artist, event);
                  }}
                  onTouchEnd={clearSidebarLongPress}
                  onTouchCancel={clearSidebarLongPress}
                  onTouchMove={clearSidebarLongPress}
                  avatar={
                    <div className="artist-avatar" style={getProfileImageStyle(artist)}>
                      {!normalizeAvatarImageValue(artist?.profile_picture) && (artist.name?.charAt(0) || '')}
                      {isNotifyMuted ? (
                        <span className="artist-mute-badge" aria-hidden="true" title="通知オフ">
                          <i className="fa-solid fa-bell-slash" aria-hidden="true"></i>
                        </span>
                      ) : null}
                    </div>
                  }
                  title={artist.name}
                  titleClassName={isBlocked ? 'artist-name--muted' : ''}
                  titleExtra={
                    <>
                      {isPinnedArtist(artist) ? (
                        <span className="artist-pin-indicator" aria-label="固定済み">
                          <i className="fa-solid fa-thumbtack" aria-hidden="true"></i>
                        </span>
                      ) : null}
                      {artist.statusLabel ? (
                        <span className="status-label status-label--danger">{artist.statusLabel}</span>
                      ) : null}
                      {artist.part_short_name ? <span className="artist-part">（{artist.part_short_name}）</span> : null}
                    </>
                  }
                  preview={isMessageHit ? undefined : preview}
                  previewNode={
                    isMessageHit ? (
                      <div className="artist-last-preview">
                        {renderChatSearchHighlight(preview, messageFilter)}
                      </div>
                    ) : undefined
                  }
                  belowContent={
                    mentionTask?.count > 0 ? (
                      <ChatMentionTaskNotice
                        count={mentionTask.count}
                        disabled={
                          resolvingMentionTargetId === normalizeArtistId(artist.artist_id) ||
                          loadingMentionResolutionTargetKey === `personal:${normalizeArtistId(artist.artist_id)}`
                        }
                        onResolve={() =>
                          openMentionResolution({
                            scope: 'personal',
                            targetId: artist.artist_id,
                            count: mentionTask.count,
                            title: artist.name ? `${artist.name} の未対応メンション` : '未対応メンション',
                            resolveTarget: resolveMentionTarget,
                          })
                        }
                        onOpen={() =>
                          handleArtistClick(artist, {
                            anchorMessageId: String(mentionTask.messageId || '').trim(),
                          })
                        }
                      />
                    ) : null
                  }
                  time={timeLabel}
                  badge={!isMessageHit && showUnreadBadge ? (unreadCount > 99 ? '99+' : unreadCount) : null}
                  badgeClassName={!isMessageHit && showUnreadBadge ? unreadBadgeToneClass : ''}
                />
              );
            })}
            {!artistsLoading && displayedArtists.length === 0 ? (
              <li className="side-list-empty">
                {isTargetSearchMode
                  ? '一致するチャットはありません。条件を変えるか「解除」してください。'
                  : '該当するチャットがありません。'}
              </li>
            ) : null}
            <li ref={artistsSentinelRef} style={{ height: 1 }} />
          </ul>
          {canSwitchToGroupChat ? (
            <ChatTargetSwitchFab
              label="グループチャットへ切り替え"
              iconClassName="fa-solid fa-users"
              unreadCount={adminGroupChatMessageCount}
              badgeToneClassName={
                adminGroupChatTone === 'ok'
                  ? 'sidebar-count-badge--ok'
                  : 'sidebar-count-badge--alert'
              }
              onClick={handleOpenGroupChatFromTargetList}
            />
          ) : null}
        </div>

        {/* ===== Right: Chat area + Side panel ===== */}
        <div className={`chat-main-with-side${isSidePanelLayoutOpen ? '' : ' is-side-panel-collapsed'}`}>
        <div className="admin-message-chat-container chat-area chat-dnd-scope" ref={chatContainerOuterRef}>
          {isAiTargetSelected ? (
            <ChatFollowupInbox
              initialSummary={followupSummary}
              refreshKey={followupRefreshKey}
              onOpenTask={handleOpenAiTask}
              onBack={goBackMobile}
              showBackButton={Boolean(isMobile && mobileView === 'chat')}
            />
          ) : !selectedArtist ? (
            <ChatEmptyState ariaLabel="アーティストとのチャットが未選択です" />
          ) : (
            <>
              <ChatAreaHeader
                className="admin-message-chat-header"
                showBackButton={Boolean(isMobile && mobileView === 'chat')}
                onBack={goBackMobile}
                backButtonClassName={mobileBackButtonClassName}
                main={(
                  <button
                    type="button"
                    className="profile-info profile-link"
                    onClick={handleGoArtistDetail}
                    title="アーティスト詳細を開く"
                    aria-label={selectedArtist.name ? `${selectedArtist.name}の詳細を開く` : 'アーティスト詳細を開く'}
                    disabled={!selectedArtistId}
                  >
                    <div className="profile-image" style={getProfileImageStyle(selectedArtist)}>
                      {!normalizeAvatarImageValue(selectedArtist?.profile_picture) && (selectedArtist.name?.charAt(0) || '')}
                    </div>
                    <div className="artist-info">
                      <span className="artist-info__name">{selectedArtist.name}</span>
                      {selectedArtist.statusLabel ? (
                        <span className="status-label status-label--danger">{selectedArtist.statusLabel}</span>
                      ) : null}
                      {(selectedArtist.part_short_name || selectedArtist.part_label) ? (
                        <span className="artist-part">（{selectedArtist.part_short_name || selectedArtist.part_label}）</span>
                      ) : null}
                    </div>
                  </button>
                )}
                actionsClassName="admin-message-chat-header__actions chat-header-action-group"
                actions={(
                  <>
                    {canViewPersonalNotes ? (
                      <ChatHeaderIconButton
                        className="settings-button chat-header-icon-button"
                        onClick={() => setShowNotes((current) => !current)}
                        aria-label={showNotes ? 'チャットに戻る' : 'ノート'}
                        title={showNotes ? 'チャットに戻る' : 'ノート'}
                        iconClassName={`fa-solid ${showNotes ? 'fa-comment' : 'fa-note-sticky'}`}
                        badgeCount={showNotes ? 0 : noteCount}
                      />
                    ) : null}
                    <ChatHeaderOverflowMenu
                      items={[
                        { key: 'links', icon: 'fa-link', label: 'リンク', onClick: () => setShowLinksModal(true) },
                        { key: 'media', icon: 'fa-photo-film', label: '写真・ファイル', onClick: () => setShowMediaModal(true) },
                        { key: 'settings', icon: 'fa-gear', label: 'チャット設定', onClick: handleSettingsButtonClick },
                      ]}
                    />
                  </>
                )}
              />

              {!showNotes ? (
                <ChatAnnouncementBar
                  announcement={latestAnnouncement}
                  count={announcementCount}
                  onOpenList={() => setIsAnnouncementListOpen(true)}
                />
              ) : null}

              {!showNotes && showNewMsgBanner && (
                <div className="new-message-banner" onClick={handleBannerClick}>
                  <i className="fa-solid fa-comment" style={{ fontSize: 18, marginRight: 10 }}></i>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>タクティカートから新着メッセージが届きました</span>
                </div>
              )}

              <ChatTimelineLoading visible={!showNotes && showDelayedInitialLoading && !shouldShowChatGuidance && !sendingBusy} />
              {!showNotes && sendingBusy && (
                <div className="chat-loading-overlay" aria-label="送信中">
                  <div className="chat-spinner" aria-hidden="true"></div>
                </div>
              )}

              {showNotes && canViewPersonalNotes ? (
                <AdminPersonalNotesPanel
                  artistId={selectedArtist.artist_id}
                  onModeChange={() => {}}
                  onNotesChanged={refreshNoteCount}
                  canCreateNote={canManagePersonalNotes}
                  canManageNotes={canManagePersonalNotes}
                />
              ) : (
              <div className="admin-message-chat-messages chat-message-stack" ref={chatContainerRef}>
                {withUnreadDivider(displayMessages.map((message) => {
                  const rawBubbleClass = message.className || getMessageClass(message);
                  const isSystemMessage = message.isSystem ?? isSystemFormat(message.message_format);
                  const isNoteMessage = isGroupNoteMessage(message);
                  const flyerCacheKey = String(message.id || `${message.message_format}-${message.created_at}`);
                  const cachedFlyerUrl = getCachedFlyerUrl(flyerCacheKey);
                  const showCardIcon = false;
                  const systemToneClass = getSystemToneClass(message);
                  const presentation = buildAdminIndividualMessagePresentation({
                    message,
                    currentAdminId: loggedInAdminId,
                    isSystemMessage,
                    artistName: selectedArtist?.name || message.artist_name,
                    resolveAdminName: resolveAdminSenderDisplayName,
                  });
                  const { isAdminMessage, isOwnStandardMessage, adminSenderName, leftSenderName } = presentation;
                  const bubbleClass = resolveAdminIndividualBubbleClassName({
                    className: rawBubbleClass,
                    isOwnStandardMessage,
                    isSystemMessage,
                  });
                  const leftSenderContent = isAdminMessage
                    ? <AdminSenderName name={leftSenderName} />
                    : leftSenderName;
                  const showAdminSenderInMeta =
                      isSystemMessage &&
                      shouldShowAdminSenderForMessage(message) &&
                      Boolean(adminSenderName);
                  const messageTimeText = formatDateTimeJST(message.created_at);
                  const normalizedMessageId = normalizeMessageId(message?.id);
                  const hasCopyFocus = Boolean(
                    normalizedMessageId &&
                    inlineCopyMessageId &&
                    inlineCopyMessageId === normalizedMessageId
                  );
                  const shouldDimRow = Boolean(
                    inlineCopyMessageId &&
                    normalizedMessageId &&
                    inlineCopyMessageId !== normalizedMessageId
                  );
                  if (isNoteMessage) {
                    const noteAuthorIsAdmin = isAdminMessage || String(message.sender_type || '').toLowerCase() === 'admin';
                    const noteAuthorName = noteAuthorIsAdmin
                      ? adminSenderName
                      : (selectedArtist?.name || message.artist_name || 'アーティスト');
                    const noteArtistAvatarNode = !noteAuthorIsAdmin ? (
                      <span
                        className="chat-avatar"
                        style={getProfileImageStyle(selectedArtist || {})}
                        aria-hidden="true"
                      >
                        {!normalizeAvatarImageValue(selectedArtist?.profile_picture) &&
                          (selectedArtist?.name?.charAt(0) || message.artist_name?.charAt(0) || '')}
                      </span>
                    ) : null;
                    return (
                      <ChatNoteSystemMessageRow
                        key={normalizeMessageId(message?.id) || getMessageDedupKey(message)}
                        message={message}
                        variant="plain"
                        preset="adminIndividual"
                        avatarNode={noteAuthorIsAdmin ? renderAdminSenderAvatar(message) : noteArtistAvatarNode}
                        senderName={noteAuthorName}
                        senderContent={noteAuthorIsAdmin ? <AdminSenderName name={noteAuthorName} /> : noteAuthorName}
                        authorIsAdmin={noteAuthorIsAdmin}
                        bubbleClassName="admin-message system-message"
                        timeLabel={messageTimeText}
                        activeAnchorMessageId={activeAnchorMessageId}
                        onOpenNote={canViewPersonalNotes ? ((noteId) => {
                          setShowNotes(true);
                          if (noteId) {
                            window.setTimeout(() => {
                              window.dispatchEvent(
                                new CustomEvent('admin-personal-notes-open-detail', { detail: { noteId } })
                              );
                            }, 0);
                          }
                        }) : undefined}
                      />
                    );
                  }
                  const renderedSystemMessage = renderIndividualSystemMessage({
                    message,
                    isSystemMessage,
                    showCardIcon,
                    systemToneClass,
                    formatMessageContent,
                    parseReminderContent,
                    cachedFlyerUrl,
                    buildFlyerUrl,
                    pickFlyerFilename,
                    resolveGenreBadge: (genre) => {
                      const primaryGenre = resolvePrimaryGenreObj(genre);
                      if (!primaryGenre) return null;
                      return {
                        label: getGenreLabel(genre),
                        backgroundColor: getGenreColor(genre),
                        color: getGenreTextColor(genre),
                      };
                    },
                    formatEventDate: toYmdSlash,
                    formatEventTime: (value) => String(value || '').slice(0, 5),
                    titles: {
                      partNotice: 'お知らせを送りました',
                      stageSeatingShared: 'ステージシーティングを共有しました',
                      hold: 'オファーを送りました',
                      holdCancelled: 'オファーをキャンセルしました',
                      contract: '本契約を送信しました',
                    },
                    links: {
                      contractDetailHref: (msg) =>
                        msg?.contract_artist_id
                          ? `/admin/workspaces/contracts/${msg.contract_artist_id}`
                          : '',
                      holdDetailHref: getHoldDeclineLink,
                      eventDetailHref: (eventUuid) =>
                        eventUuid ? `/admin/workspaces/event-operations/events/${eventUuid}` : '',
                      holdDeclineHref: getHoldDeclineLink,
                      conciergeFallbackHref: '/artist/workspaces/concierge/dashboard',
                    },
                    extractFirstUrl,
                  });
                  const systemAvatarNode = renderedSystemMessage ? (
                    <ChatAnnouncementAvatar
                      toneClassName={getSystemAvatarToneClass(message)}
                      iconClass={getSystemAvatarIconClass(message)}
                    />
                  ) : null;
                  const artistAvatarNode = !isAdminMessage && !isSystemMessage ? (
                    <span
                      className="chat-avatar"
                      style={getProfileImageStyle(selectedArtist || {})}
                      aria-hidden="true"
                    >
                      {!normalizeAvatarImageValue(selectedArtist?.profile_picture) &&
                        (selectedArtist?.name?.charAt(0) || message.artist_name?.charAt(0) || '')}
                    </span>
                  ) : null;
                  const avatarNode =
                    systemAvatarNode ||
                    (!isOwnStandardMessage && isAdminMessage ? renderAdminSenderAvatar(message) : null) ||
                    artistAvatarNode;
                  return (
                    <IndividualStandardMessageRow
                      key={normalizeMessageId(message?.id) || getMessageDedupKey(message)}
                      message={message}
                      variant="plain"
                      preset="adminIndividual"
                      isSelf={isOwnStandardMessage}
                      senderName={leftSenderName}
                      senderContent={leftSenderContent}
                      showSender={Boolean(leftSenderName)}
                      readCount={isOwnStandardMessage && isAdminPersonalOutgoingRead(message) ? 1 : 0}
                      readLabel="既読"
                      timeLabel={messageTimeText}
                      avatarNode={avatarNode}
                      bubbleClassName={`${bubbleClass}${hasCopyFocus ? ' chat-message-row__bubbleWrap--copyActive' : ''}`}
                      metaClassName={bubbleClass}
                      rowClassName={
                        hasCopyFocus
                          ? 'chat-message-row--copyActive'
                          : shouldDimRow
                            ? 'chat-message-row--copyDimmed'
                            : ''
                      }
                      metaExtra={
                        showAdminSenderInMeta
                          ? renderAdminSenderMeta(message)
                          : null
                      }
                      activeAnchorMessageId={activeAnchorMessageId}
                      onContextMenu={inlineCopyMessageId ? undefined : (e, target = message) => handleMessageContextMenu(e, target)}
                      onTouchStart={inlineCopyMessageId ? undefined : (e) => handleMessageTouchStart(e, message)}
                      onTouchEnd={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      onTouchCancel={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      onTouchMove={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      renderMessageContent={() =>
                        renderPartialCopyContent(
                          message,
                          renderedSystemMessage ?? (
                            (() => {
                              const resolvedImage =
                                message.message_format === ADMIN_MESSAGE_FORMATS.IMAGE
                                  ? resolveIndexedImagePreview({
                                      message,
                                      imageIndexByMessageId,
                                      imageMessages,
                                      imagePreviewUrls,
                                      buildPreviewUrl: (fileName) => buildMessageImageUrl(fileName, 'proxy'),
                                    })
                                  : { safeIndex: -1, previewSrc: '' };

                              return renderIndividualStandardMessageContent({
                                message,
                                replyContent: message.reply ? (
                                  <ChatReplySnippet
                                    reply={message.reply}
                                    onClick={() => handleJumpToReplyMessage(message.reply)}
                                  />
                                ) : null,
                                formatMessageContent,
                                formatExpireDateLabel: formatDateJPFromAny,
                                fileName: message.original_name || message.stored_file_name || '',
                                fileUrl: message.file_id
                                  ? `${API_URL}/api/admin/messages/files/${message.file_id}/download?proxy=stream`
                                  : '',
                                canDownloadFile: !!message.stored_file_name,
                                onDownloadFile: () => handleDownload('file', message.file_id),
                                imagePreviewSrc: resolvedImage.previewSrc,
                                onOpenImage: () => handleImageClick(resolvedImage.safeIndex),
                                onDownloadImage: () => handleDownload('image', message.image_id),
                                imageGroupItems: message.image_group_items,
                                onMentionClick: handleMentionClick,
                                buildImageGroupPreviewItem: (item) => {
                                  const indexed = resolveIndexedImagePreview({
                                    message: item,
                                    imageIndexByMessageId,
                                    imageMessages,
                                    imagePreviewUrls,
                                    buildPreviewUrl: (fileName) => buildMessageImageUrl(fileName, 'proxy'),
                                  });
                                  return {
                                    messageId: item.id,
                                    imageId: item.image_id,
                                    src: indexed.previewSrc,
                                    width: item.image_width,
                                    height: item.image_height,
                                    modalIndex: indexed.safeIndex,
                                  };
                                },
                                onOpenImageGroupItem: (item) => handleImageClick(item.modalIndex),
                              });
                            })()
                          ),
                          isOwnStandardMessage
                        )
                      }
                      sideActionContent={
                        !inlineCopyMessageId &&
                        !isSystemMessage && !isAdminMessage && supportsChatMessageReaction(message) ? (
                          <ChatMessageReactionBar
                            messageId={message.id}
                            reactions={message.reactions}
                            canReact
                            showReactionChips={false}
                            onToggleReaction={toggleReaction}
                            onOpenDetail={openReactionDetail}
                            isPending={isReactionPending}
                            compact
                          />
                        ) : null
                      }
                      metaEndContent={
                        !isSystemMessage &&
                        Array.isArray(message.reactions) &&
                        message.reactions.length > 0 ? (
                          <ChatMessageReactionBar
                            messageId={message.id}
                            reactions={message.reactions}
                            canReact={false}
                            onOpenDetail={openReactionDetail}
                            isPending={isReactionPending}
                            showAddButton={false}
                          />
                        ) : null
                      }
                    />
                  );
                }), displayMessages, unreadDividerMessageId)}
                {loadingOlderMessages && hasMore && (
                  <div className="chat-paging-loading" aria-label="追加読み込み中">
                    <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
                  </div>
                )}
                {shouldShowChatGuidance ? <AdminArtistChatGuidanceMessage /> : null}
                <ChatDragOverlay show={isDragActive} rect={overlayRect} />
              </div>
              )}

              {!showNotes ? (
              <ChatComposer
                mode="admin"
                scope="individual"
                artistId={selectedArtist.artist_id}
                onSent={handleComposerSent}
                onUploading={handleUploading}
                replyDraft={replyDraft}
                onReplyCancel={() => setReplyDraft(null)}
              onReplyChange={setReplyDraft}
                mentionCandidates={personalMentionCandidates}
                readOnly={isReadOnly}
                onSendBlocked={(msg) => {
                  if (toastError) {
                    toastError(msg || '現在メンテナンス中のため送信できません。');
                  }
                }}
              />
              ) : null}
            </>
          )}
        </div>

        {shouldShowSidePanel ? (
          <aside
            className={`chat-side-panel side-panel admin-chat-side-panel${
              isSidePanelOpen ? '' : ' chat-side-panel--collapsed'
            }`}
          >
            {isSidePanelOpen ? (
              <>
                <ChatSidePanelHeader
                  icon="fa-user"
                  label="アーティスト情報"
                  onCollapse={() => setIsSidePanelOpen(false)}
                />
                <ArtistProfileSidePanelCard
                  artist={selectedArtist}
                  detail={artistDetail}
                  isLoading={artistDetailLoading}
                  avatarUrl={
                    normalizeAvatarImageValue(selectedArtist?.profile_picture)
                      ? appendVariant(
                          buildAssetUrl(
                            ARTIST_PROFILE_IMAGE_PATH,
                            normalizeAvatarImageValue(selectedArtist?.profile_picture)
                          ),
                          'proxy'
                        )
                      : ''
                  }
                  avatarStyle={getProfileImageStyle(selectedArtist)}
                  onAvatarClick={openSelectedArtistProfile}
                />

                <AnnouncementSummarySidePanelCard
                  title="オファー"
                  rows={announcementSummaries.offers}
                  isLoading={isSidePanelLoading}
                  emptyText="まだオファーの送信履歴がありません。"
                  formatDate={formatDateJPFromAny}
                />

                <AnnouncementSummarySidePanelCard
                  title="本契約"
                  rows={announcementSummaries.contracts}
                  isLoading={isSidePanelLoading}
                  emptyText="まだ本契約の送信履歴がありません。"
                  formatDate={formatDateJPFromAny}
                />
              </>
            ) : (
              <div className="chat-side-panel__collapsed-rail">
                <ChatSidePanelToggleButton
                  isOpen={false}
                  onToggle={() => setIsSidePanelOpen(true)}
                  label="アーティスト情報"
                  iconOnly
                  className="chat-side-panel__collapsed-toggle"
                />
              </div>
            )}
          </aside>
        ) : null}
        </div>
        </ChatWorkspaceLayout>

        <ChatPageSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onApply={applyTargetSearchModal}
          onClear={clearTargetSearchModal}
          summary={searchSummaryLabel}
          searchDraft={searchDraft}
          setSearchDraft={setSearchDraft}
          parts={parts}
          groups={groups}
        />

        <ChatPageSettingsModal
          isOpen={showSettingsModal}
          onClose={closeChatSettingsModal}
          settingsArtist={settingsArtist}
          selectedArtist={selectedArtist}
          isNicknameAllowed={isNicknameAllowed}
          onNicknamePermissionChange={handleNicknamePermissionChange}
          isReadNotifyEnabled={isReadNotifyEnabled}
          onReadNotifyToggle={handleReadNotifyToggle}
          readToggleLoading={readToggleLoading}
          artistChatMuted={artistChatMuted}
          onArtistMuteToggle={handleArtistMuteToggle}
          artistChatMuteLoading={artistChatMuteLoading}
          readNotifyFetching={readNotifyFetching}
          readEnabledAdmins={readEnabledAdmins}
        />

        <ChatImageModal
          isOpen={isModalOpen}
          src={imageFullUrls[currentImageIndex]}
          downloadItems={imageModalItems}
          currentIndex={currentImageIndex}
          onClose={closeModal}
          closeOnBack={false}
          onPrev={showPrevImage}
          onNext={showNextImage}
          showNav={imageFullUrls.length > 1}
        />

        {targetMenu && (
          <div className="message-menu-overlay" onClick={closeTargetMenu}>
            <div
              ref={targetMenuRef}
              className="message-menu target-menu" role="menu" aria-label="チャットの操作"
              style={{ top: targetMenu.y, left: targetMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <PersonalMemoMenuItem target={{kind:'chat_individual',id:String(targetMenu.artist.artist_id)}} name={targetMenu.artist.name||''} className="message-menu__item" onAfterOpen={closeTargetMenu}/>
              <button
                type="button" role="menuitem"
                className="message-menu__item"
                onClick={handleTogglePinnedFromTargetMenu}
              >
                <i className="fa-solid fa-thumbtack" aria-hidden="true" />
                <span>{isPinnedArtist(targetMenu.artist) ? '固定解除' : '固定'}</span>
              </button>
              <button
                type="button" role="menuitem"
                className="message-menu__item"
                onClick={handleOpenSettingsFromTargetMenu}
              >
                <i className="fa-solid fa-gear" aria-hidden="true" />
                <span>チャット設定</span>
              </button>
            </div>
          </div>
        )}

        <ChatFollowupNotificationMenu
          menu={followupNotificationMenu}
          onClose={closeFollowupNotificationMenu}
          toastError={toastError}
          toastSuccess={toastSuccess}
        />

        <ChatMessageActionMenu menu={messageMenu} menuRef={messageMenuRef} onClose={closeMessageMenu}

          reactions={messageMenu && canReactMessage(messageMenu.message) ? (

            <ChatMessageMenuReactionPicker

              messageId={messageMenu.message?.id}

              reactions={messageMenu.message?.reactions}

              onSelectReaction={handleMessageMenuReactionSelect}

              isPending={isReactionPending}

            />

          ) : null}>
          {messageMenu && <>
              {canReplyMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  onClick={handleSelectReplyMessage}
                >
                  <i className="fa-solid fa-reply" aria-hidden="true" />
                  <span>返信する</span>
                </button>
              ) : null}
              {canCopyMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  data-chat-action="copy" onClick={handleCopyMessage}
                >
                  <i className="fa-solid fa-copy" aria-hidden="true" />
                  <span>コピー</span>
                </button>
              ) : null}
              {canCopyMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  onClick={handleOpenInlinePartialCopy}
                >
                  <i className="fa-solid fa-i-cursor" aria-hidden="true" />
                  <span>部分コピー</span>
                </button>
              ) : null}
              {canCreateQuickTaskFromMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  onClick={handleOpenQuickTaskFromMessage}
                >
                  <i className="fa-solid fa-list-check" aria-hidden="true" />
                  <span>タスクにする</span>
                </button>
              ) : null}
              {canAnnouncementMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  onClick={handleToggleAnnouncementMessage}
                >
                  <i
                    className={`fa-solid ${
                      announcementActionMessageId === String(messageMenu.message?.id || '')
                        ? 'fa-spinner fa-spin'
                        : isAnnouncedMessage(messageMenu.message?.id)
                          ? 'fa-bullhorn-slash'
                          : 'fa-bullhorn'
                    }`}
                    aria-hidden="true"
                  />
                  <span>
                    {announcementActionMessageId === String(messageMenu.message?.id || '')
                      ? '処理中…'
                      : isAnnouncedMessage(messageMenu.message?.id)
                        ? 'アナウンス解除'
                        : 'アナウンスに設定'}
                  </span>
                </button>
              ) : null}
              {canUnsendMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item message-menu__item--danger"
                  onClick={handleUnsendMessage}
                >
                  送信取り消し
                </button>
              ) : null}

          </>}
        </ChatMessageActionMenu>

        <ChatAnnouncementListModal
          open={isAnnouncementListOpen}
          announcements={announcements}
          onClose={() => setIsAnnouncementListOpen(false)}
          onRemove={unpinAnnouncement}
          removingMessageId={announcementActionMessageId}
          onSelect={handleSelectAnnouncementMessage}
        />

        <ChatMessageReactionDetailModal
          open={isReactionDetailOpen}
          loading={reactionDetailLoading}
          groups={reactionDetailGroups}
          onClose={closeReactionDetail}
        />

        <ChatMentionTaskResolutionModal {...mentionResolutionModalProps} />

        <ChatLinksModal
          isOpen={showLinksModal}
          loadLinks={chatAssetSource?.loadLinks}
          onClose={() => setShowLinksModal(false)}
        />
        <ChatMediaModal
          isOpen={showMediaModal}
          loadMedia={chatAssetSource?.loadMedia}
          downloadZip={chatAssetSource?.downloadZip}
          buildFileUrl={chatAssetSource?.buildFileUrl}
          onClose={() => setShowMediaModal(false)}
        />

        <QuickTaskModal
          open={Boolean(quickTaskSource)}
          source={quickTaskSource}
          onClose={() => setQuickTaskSource(null)}
          onCreated={() => toastSuccess?.('クイックタスクを作成しました。')}
        />

        <ChatProfileModalLayer
          profileTarget={profileTarget}
          onClose={closeProfileModal}
          allowArtistLink
        />
      </div>
    </div>);
}
